package com.devi3east.bashbuddy.dashboard.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.indication
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material3.Badge
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.common.TrailingActionIcon
import com.devi3east.bashbuddy.features.common.util.formatPrice
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.common.data.models.Ticket
import com.devi3east.bashbuddy.features.events.common.data.models.TicketType
import com.devi3east.bashbuddy.features.events.requests.CreateDraftTicketRequest

@Composable
fun OrganizerDashboardCard(
    event: Event,
    eventTickets: List<Ticket>,
    isExpanded: Boolean,
    eventPending: Int,
    onExpandToggle: (String) -> Unit,
    onEditEvent: (String) -> Unit,
    onDeleteEvent: (String) -> Unit,
    onNavigateToDetail: (String, String) -> Unit,
    onCreateTicket: (String, CreateDraftTicketRequest) -> Unit,
    onPublishEvent: (String) -> Unit,
    onEditTicket: (String) -> Unit,
    onDeleteTicket: (String) -> Unit,
    onAdjustTicket: (String) -> Unit,
    onTogglePauseTicket: (String, Boolean) -> Unit,
    pendingAssignmentsPerTicket: Map<String, Int>,
    navigateToTicketDetail: (String) -> Unit,
    navigateToPrizeList: (String, String) -> Unit
) {
    val cardShape = RoundedCornerShape(16.dp)
    val interactionSource = remember { MutableInteractionSource() }

    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp)
            .clip(cardShape)
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.1f),
                shape = cardShape
            )
            .indication(interactionSource = interactionSource, indication = LocalIndication.current)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = { onExpandToggle(event._id) }
            ),
        shape = cardShape,
        elevation = CardDefaults.elevatedCardElevation(),
        colors = CardDefaults.elevatedCardColors(containerColor = MaterialTheme.colorScheme.surface)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    val displayTitle = event.title?.let {
                        if (it.length > 22) it.take(22) + "…" else it
                    } ?: "Draft Event"
                    Text(
                        text = displayTitle,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1
                    )
                    if (eventPending > 0) {
                        Spacer(modifier = Modifier.width(6.dp))
                        Badge { Text(eventPending.toString()) }
                    }
                }
                TrailingActionIcon(
                    onClick = { onNavigateToDetail(event._id, event.slug) },
                    icon = Icons.AutoMirrored.Filled.ArrowForward,
                    iconTint = MaterialTheme.colorScheme.primary,
                    backgroundColor = MaterialTheme.colorScheme.primaryContainer
                )
            }
            Spacer(Modifier.height(8.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row {
                    IconButton(
                        onClick = { onDeleteEvent(event._id) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.error.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = "Delete Event",
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                    Spacer(Modifier.width(8.dp))
                    IconButton(
                        onClick = { onEditEvent(event._id) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.Edit,
                            contentDescription = "Edit Event",
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }
                    Spacer(Modifier.width(8.dp))
                    IconButton(
                        onClick = { navigateToPrizeList(event._id, event.slug) },
                        modifier = Modifier.background(
                            color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.1f),
                            shape = RoundedCornerShape(8.dp)
                        )
                    ) {
                        Icon(
                            imageVector = Icons.Default.EmojiEvents,
                            contentDescription = "Prize List",
                            tint = MaterialTheme.colorScheme.tertiary
                        )
                    }
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        text = "Revenue: ${formatPrice(event.revenue)}",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.secondary
                    )
                    Text(
                        text = "Tickets Sold: ${event.ticketsSold}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            AnimatedVisibility(
                visible = isExpanded,
                enter = expandVertically(),
                exit = shrinkVertically()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 12.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Button(
                            onClick = { onPublishEvent(event._id) },
                            enabled = event.publishedAtInstant == null,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.primary,
                                contentColor = MaterialTheme.colorScheme.onPrimary
                            )
                        ) {
                            Text("Publish", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold))
                        }
                        Button(
                            onClick = {
                                val request = CreateDraftTicketRequest(ticketType = TicketType.DIGITAL)
                                onCreateTicket(event._id, request)
                            },
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.primary,
                                contentColor = MaterialTheme.colorScheme.onPrimary
                            )
                        ) {
                            Text("Add Ticket", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold))
                        }
                    }

                    Spacer(Modifier.height(8.dp))
                    eventTickets.forEachIndexed { index, ticket ->
                        val ticketPending = pendingAssignmentsPerTicket[ticket._id] ?: 0

                        DashboardTicketCard(
                            ticketId = ticket._id,
                            ticketType = ticket.ticketType,
                            status = ticket.status,
                            title = ticket.title ?: "Draft Ticket",
                            revenue = ticket.revenue,
                            ticketsSold = ticket.ticketsSold,
                            maxQuantity = ticket.maxQuantity ?: 0,
                            onEdit = onEditTicket,
                            onDelete = onDeleteTicket,
                            onAdjust = onAdjustTicket,
                            pendingAssignments = ticketPending,
                            onTogglePause = onTogglePauseTicket,
                            onAssignClick = { navigateToTicketDetail(ticket._id) }
                        )
                        if (index < eventTickets.lastIndex) HorizontalDivider(Modifier.padding(vertical = 8.dp))
                    }
                }
            }
        }
    }
}